import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

export type ConfigurationScopeIdUnbranded = string;
export const ConfigurationScopeIdSchema =
  looseUuid().brand<"haven-common:ConfigurationScopeId">() satisfies ZodType<ConfigurationScopeIdUnbranded>;
/**
 * Branded apart from `OrganisationId` because both are holders of settings and
 * are passed to adjacent arguments, and because the two are deliberately
 * different kinds of thing: a scope confers configuration, an organisation is
 * something people hold memberships at.
 */
export type ConfigurationScopeId = z.infer<typeof ConfigurationScopeIdSchema>;

/**
 * A level of the configuration chain above an organisation — a national
 * programme, a regional cooperative, a pilot cohort.
 *
 * Its own entity rather than a parent on `organisations`, which stay flat. That
 * separation is the whole reason a chain can exist here at all: a parent id on an
 * organisation is the first thing an access rule would reach for, and reach over
 * a young person's record follows the organisation that registered it. A scope
 * confers configuration and nothing else — no authorisation rule may walk this
 * chain. See
 * `docs/decisions/2026-09-11-a-configuration-scope-confers-no-reach.md`.
 *
 * One parent, not several. Two parents would need a precedence rule between them,
 * and there is nothing yet to say what it should be.
 */
export type ConfigurationScopeUnbranded = {
  id: ConfigurationScopeIdUnbranded;
  parentScopeId: ConfigurationScopeIdUnbranded | null;
  name: string;
};

export const ConfigurationScopeSchema = z
  .object({
    id: ConfigurationScopeIdSchema,
    parentScopeId: ConfigurationScopeIdSchema.nullable(),
    name: z.string().min(1).max(255),
  })
  .brand<"haven-common:ConfigurationScope">() satisfies ZodType<ConfigurationScopeUnbranded>;

export type ConfigurationScope = z.infer<typeof ConfigurationScopeSchema>;

/**
 * How far up a chain a walk will go before giving up.
 *
 * A guard against a chain that closed on itself rather than a limit anyone is
 * expected to reach: resolution walks up from a body, and a cycle would not
 * produce a wrong answer, it would run until something gave out. Eight is far
 * past any structure the domain has described — a programme, a region, a
 * cooperative, a cohort — so a chain hitting it is a fault rather than a body
 * that has outgrown the model.
 */
export const CONFIGURATION_SCOPE_DEPTH_MAX = 8;

/**
 * A name as somebody may set it, which is stricter than what a stored row may
 * hold — trimmed before it is measured, so a scope named only of spaces is
 * refused rather than stored as one. The messages are translation keys, following
 * `UserNameSchema`: this schema marks the field in the browser and refuses the
 * write on the server, and only one of those has a locale to render words from.
 */
export const ConfigurationScopeNameSchema = z
  .string()
  .trim()
  .min(1, "admin.configuration.scope.name.required")
  .max(255, "admin.configuration.scope.name.tooLong") satisfies ZodType<string>;
